import React, { useState, useEffect } from "react";
import Layout from "../components/Layout/Layout";
import { useUser } from "../contexts/UserContext";
import { useClient } from "../contexts/ClientContext";
import api from "../utils/api";
import DashboardExcelExportButton from "../components/DashboardExcelExportButton";
import DatePicker from "react-datepicker";
import Select from "react-select";

import TicketDashboard from "../components/TicketDashboard";
import OverallTicketChart from "../components/OverallTicketChart";
import ReopenedAnalysisChart from "../components/ReopenedAnalysisChart";
import RepeatTrendChart from "../components/RepeatTrendChart";
import AverageTicketClosureTimeChart from "../charts/AverageTicketClosureTimeChart";

function Dashboard() {
  const { user } = useUser();
  const { selectedClientId, setSelectedClientId } = useClient();

  // DASHBOARD FILTERS STATE
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedPriority, setselectedPriority] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [collegeName, setcollegeName] = useState();
  const [years, setyears] = useState([]);
  const [modules, setmodules] = useState([]);
  const [priority, setpriority] = useState([]);
  const [status, setstatus] = useState([]);
  const [ticketdata, setTicketdata] = useState([]);

  const [reopenedAnalytics, setReopenedAnalytics] = useState(null);
  const [repeatTrends, setRepeatTrends] = useState(null);
  const [closureAnalytics, setClosureAnalytics] = useState(null);

  // Define fetchAnalyticsData inside useEffect or rely on user dependency
  const fetchAnalyticsData = async () => {
    // Only fetch for Admin (Role ID 1)
    if (user && String(user.role_id) === '1') {
      try {
        const res = await api.get("/ticket/getAnalyticsData", {
          params: {
            clientId: selectedClientId,
            selectedYears,
            selectedPriority,
            selectedModules,
            fromDate,
            toDate,
          }
        });
        if (res.data.status) {
          setReopenedAnalytics(res.data.reopened_analytics);
          setRepeatTrends(res.data.repeat_trends);
          setClosureAnalytics(res.data.closure_analytics);
        }
      } catch (err) {
        console.error("Error fetching analytics data:", err);
      }
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [
    user,
    selectedClientId,
    selectedYears,
    selectedPriority,
    selectedModules,
    fromDate,
    toDate,
  ]);

  const [dropdown1, setDropdown1] = useState("");
  const [dropdown2, setDropdown2] = useState("");

  const [categories, setcategories] = useState(false);

  const [series, setseries] = useState(false);

  const [seriesToExcelArray, setseriesToExcelArray] = useState(false);
  const [ticket_report_data, setticket_report_data] = useState(false);

  const [Fromtodate, setFromtodate] = useState([]);
  const [page, setPage] = useState(1);

  const minPriorityDate =
    Fromtodate && Fromtodate[0] ? new Date(Fromtodate[0]) : null;
  const maxPriorityDate =
    Fromtodate && Fromtodate[1] ? new Date(Fromtodate[1]) : null;

  const yearOptions = years.map((year) => ({ value: year, label: year }));

  const statusOptions = status.map(([key, value]) => ({
    value: key,
    label: value,
  }));

  const fetchOverallChartData = async () => {
    try {
      const res = await api.get("/ticket/chartData", {
        params: {
          clientId: selectedClientId,
          user_id: user?.user_id,
          selectedYears,
          selectedStatus,
          selectedPriority,
          selectedModules,
          fromDate,
          toDate,
        },
      });
      if (res.data.ticketData) {
        setTicketdata(res.data.ticketData);
      }
      setcollegeName(res.data.data.college_name);
      if (res.data.data.data) {
        const tickets = res.data.data.data;
        const ticket_report_data = res.data.data.ticket_report_data;

        // ["FEB", "JAN", "APR", ...]
        const months = Object.keys(tickets);
        setcategories(months);

        // Convert to array for iteration
        const ticketArray = Object.values(tickets);

        const sortedYears = Object.values(res.data.data.year)
          .map(Number) // convert to numbers
          .sort((a, b) => b - a); // descending

        const sortedModules = JSON.parse(res.data.data.modules);

        const sortedPriority = JSON.parse(res.data.data.priority);

        const dates_new = JSON.parse(res.data.data.dates_new);

        setyears(sortedYears);
        setmodules(sortedModules);
        setpriority(sortedPriority);

        setFromtodate(dates_new);

        const sortedStatus = Object.entries(res.data.data.status);
        setstatus(sortedStatus);

        const statusColors = {
          "PENDING VERIFICATION & CLOSURE": "#1f77b4", // blue
          "IN PROCESS": "#ff7f0e", // orange
          REOPEN: "#d62728", // green
          CLOSED: "#2ca02c", // red
          NEW: "#fffb00ff", // purple
        };

        const statuses = [
          ...new Set(
            ticketArray.flatMap((obj) =>
              Object.keys(obj).filter((k) => k !== "month" && k !== "year")
            )
          ),
        ];

        const seriesTemp = statuses.map((status) => ({
          name: status,
          data: months.map((m) => {
            const statusData = tickets[m] && tickets[m][status];
            if (!statusData) return 0;

            // total per status (sum of subtype values)
            return Object.values(statusData).reduce(
              (sum, v) => sum + (v.count ?? 0),
              0
            );
          }),
          stack: "Ticket",
          color: statusColors[status],

          // breakdown text for tooltip
          breakdown: months.map((m) => {
            const statusData = tickets[m] && tickets[m][status];
            if (!statusData) return "";
            return Object.entries(statusData)
              .map(([k, v]) => `${k}: ${v.count}`)
              .join(", ");
          }),

          tickets: months.map((m) => {
            const statusData = tickets[m] && tickets[m][status];
            if (!statusData) return "";
            return Object.entries(statusData)
              .map(([k, v]) => `${k}: ${v.ticket_id}`)
              .join(", ");
          }),
        }));

        setseries(seriesTemp);

        const seriesForExcel = months.map((month, idx) => {
          const row = { Month: month };
          let total = 0;

          seriesTemp.forEach((s) => {
            const value = s.data[idx] ?? 0;
            const tickets = s.tickets[idx] || "";

            row[s.name] = value;
            row[`${s.name} (Tickets)`] = tickets || "-";

            total += value;
          });

          row["Total"] = total;
          return row;
        });

        setseriesToExcelArray(seriesForExcel);
        setticket_report_data(ticket_report_data);

      }
    } catch (err) {
      console.error("Error fetching overallChartData:", err);
    }
  };

  useEffect(() => {
    fetchOverallChartData();
  }, [
    user?.user_id,
    selectedClientId,
    selectedYears,
    selectedStatus,
    selectedPriority,
    selectedModules,
    fromDate,
    toDate,
  ]);

  useEffect(() => {
    if (years.length > 0 && selectedYears.length === 0) {
      const currentYear = new Date().getFullYear().toString();

      if (years.includes(currentYear)) {
        setSelectedYears([currentYear]); // set current year as default
      } else {
        setSelectedYears([years[0]]); // fallback first year
      }
    }
  }, [years, selectedYears]);

  return (
    <Layout noSidebar={window.location.pathname === "/profile"}>
      {years.length === 0 ? (
        <div className="flex h-screen w-full flex-col items-center justify-center bg-gray-50">
          <div className="mb-3 text-6xl text-gray-400">📊</div>
          <p className="text-gray-600 text-lg font-semibold">
            No data available
          </p>
        </div>
      ) : (
        <>
          <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
            {/* Dropdown */}
            <div className="flex items-center gap-4">
              {/* Year multi-select */}
              <Select
                isMulti
                name="years"
                options={yearOptions}
                className="w-64 text-sm"
                classNamePrefix="select"
                value={yearOptions.filter((o) =>
                  selectedYears.includes(o.value)
                )}
                onChange={(selected) =>
                  setSelectedYears(selected.map((o) => o.value))
                }
                placeholder="Select years..."
              />

              {/* From DatePicker */}
              <DatePicker
                popperProps={{
                  strategy: "fixed",
                }}
                popperClassName="z-50"
                selected={fromDate}
                onChange={(date) => {
                  setFromDate(date);
                  setPage(1);
                }}
                placeholderText="From date →"
                dateFormat="dd/MM/yyyy"
                minDate={
                  selectedYears.length
                    ? new Date(Math.min(...selectedYears), 0, 1)
                    : minPriorityDate
                }
                maxDate={
                  selectedYears.length
                    ? new Date(Math.max(...selectedYears), 11, 31)
                    : maxPriorityDate
                }
                isClearable
                renderCustomHeader={({
                  date,
                  changeYear,
                  changeMonth,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => {
                  const year = date.getFullYear();
                  const months = (() => {
                    if (!selectedYears.includes(year)) return [];
                    let start = 0;
                    let end = 11;
                    if (year === minPriorityDate.getFullYear())
                      start = minPriorityDate.getMonth();
                    if (year === maxPriorityDate.getFullYear())
                      end = maxPriorityDate.getMonth();
                    return Array.from(
                      { length: end - start + 1 },
                      (_, i) => start + i
                    );
                  })();

                  return (
                    <div className="flex items-center justify-between px-2 py-1">
                      <button
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        className="px-2 text-gray-600 hover:text-black"
                      >
                        ‹
                      </button>

                      <div className="flex gap-2">
                        {/* Month dropdown */}
                        <select
                          value={date.getMonth()}
                          onChange={({ target: { value } }) =>
                            changeMonth(Number(value))
                          }
                          className="border rounded px-2 py-0.5 text-sm font-bold appearance-none bg-white"
                          style={{ backgroundImage: "none" }}
                        >
                          {months.map((month) => (
                            <option key={month} value={month}>
                              {
                                [
                                  "January",
                                  "February",
                                  "March",
                                  "April",
                                  "May",
                                  "June",
                                  "July",
                                  "August",
                                  "September",
                                  "October",
                                  "November",
                                  "December",
                                ][month]
                              }
                            </option>
                          ))}
                        </select>

                        {/* Year dropdown */}
                        <select
                          value={year}
                          onChange={({ target: { value } }) =>
                            changeYear(Number(value))
                          }
                          className="border rounded px-1 py-0.5 text-sm font-bold appearance-none bg-white"
                          style={{ backgroundImage: "none" }}
                        >
                          {selectedYears.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>

                      <button
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        className="px-2 text-gray-600 hover:text-black"
                      >
                        ›
                      </button>
                    </div>
                  );
                }}
                className="pl-4 py-2 border border-gray-200 rounded w-full focus:outline-none focus:ring-1 text-sm"
              />

              {/* To DatePicker */}
              <DatePicker
                popperProps={{
                  strategy: "fixed",
                }}
                popperClassName="z-50"
                selected={toDate}
                onChange={(date) => {
                  setToDate(date);
                  setPage(1);
                }}
                placeholderText="← To date"
                dateFormat="dd/MM/yyyy"
                minDate={
                  fromDate ||
                  (selectedYears.length
                    ? new Date(Math.min(...selectedYears), 0, 1)
                    : minPriorityDate)
                }
                maxDate={
                  selectedYears.length
                    ? new Date(Math.max(...selectedYears), 11, 31)
                    : maxPriorityDate
                }
                isClearable
                renderCustomHeader={({
                  date,
                  changeYear,
                  changeMonth,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => {
                  const year = date.getFullYear();
                  const months = (() => {
                    if (!selectedYears.includes(year)) return [];
                    let start = 0;
                    let end = 11;
                    if (year === minPriorityDate.getFullYear())
                      start = minPriorityDate.getMonth();
                    if (year === maxPriorityDate.getFullYear())
                      end = maxPriorityDate.getMonth();
                    return Array.from(
                      { length: end - start + 1 },
                      (_, i) => start + i
                    );
                  })();

                  return (
                    <div className="flex items-center justify-between px-2 py-1">
                      <button
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        className="px-2 text-gray-600 hover:text-black"
                      >
                        ‹
                      </button>

                      <div className="flex gap-2">
                        {/* Month dropdown */}
                        <select
                          value={date.getMonth()}
                          onChange={({ target: { value } }) =>
                            changeMonth(Number(value))
                          }
                          className="border rounded px-2 py-0.5 text-sm font-bold appearance-none bg-white"
                          style={{ backgroundImage: "none" }}
                        >
                          {months.map((month) => (
                            <option key={month} value={month}>
                              {
                                [
                                  "January",
                                  "February",
                                  "March",
                                  "April",
                                  "May",
                                  "June",
                                  "July",
                                  "August",
                                  "September",
                                  "October",
                                  "November",
                                  "December",
                                ][month]
                              }
                            </option>
                          ))}
                        </select>

                        {/* Year dropdown */}
                        <select
                          value={year}
                          onChange={({ target: { value } }) =>
                            changeYear(Number(value))
                          }
                          className="border rounded px-1 py-0.5 text-sm font-bold appearance-none bg-white"
                          style={{ backgroundImage: "none" }}
                        >
                          {selectedYears.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>

                      <button
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        className="px-2 text-gray-600 hover:text-black"
                      >
                        ›
                      </button>
                    </div>
                  );
                }}
                className="pl-4 py-2 border border-gray-200 rounded w-full focus:outline-none focus:ring-1 text-sm"
              />

              <Select
                isMulti
                name="modules"
                options={modules}
                className="w-64 text-sm"
                classNamePrefix="select"
                value={modules.filter((o) => selectedModules.includes(o.value))}
                onChange={(selected) =>
                  setSelectedModules(selected.map((o) => o.value))
                }
                placeholder="Select module..."
              />

              <Select
                isMulti
                name="priority"
                options={priority}
                className="w-64 text-sm"
                classNamePrefix="select"
                value={priority.filter((o) =>
                  selectedPriority.includes(o.value)
                )}
                onChange={(selected) =>
                  setselectedPriority(selected.map((o) => o.value))
                }
                placeholder="Select priority..."
              />

              <Select
                isMulti
                name="status"
                options={statusOptions}
                className="w-64 text-sm"
                classNamePrefix="select"
                value={statusOptions.filter((o) =>
                  selectedStatus.includes(o.value)
                )}
                onChange={(selected) =>
                  setSelectedStatus(selected.map((o) => o.value))
                }
                placeholder="Select status..."
              />

              <DashboardExcelExportButton data={seriesToExcelArray} data2={ticket_report_data} />
            </div>

            {/* Chart Card */}
            <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition">
              <OverallTicketChart
                collegeName={collegeName}
                categories={categories}
                series={series}
              />
            </div>

            {/* Admin Analytics Section */}
            {reopenedAnalytics && repeatTrends && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ReopenedAnalysisChart
                    data={reopenedAnalytics}
                    filters={{ clientId: selectedClientId, selectedYears, selectedPriority, selectedModules, fromDate, toDate }}
                  />
                  <RepeatTrendChart
                    data={repeatTrends}
                    filters={{ clientId: selectedClientId, selectedYears, selectedPriority, selectedModules, fromDate, toDate }}
                  />
                </div>

                {closureAnalytics && (
                  <div className="grid grid-cols-1">
                    <AverageTicketClosureTimeChart
                      data={closureAnalytics}
                      filters={{ clientId: selectedClientId, selectedYears, selectedPriority, selectedModules, fromDate, toDate }}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition">
              <TicketDashboard ticketdata={ticketdata} />
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}

export default Dashboard;